import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import DocumentSlots from "./DocumentSlots";
import {
  MAX_PROFILE_ENTRIES,
  emptyEducation,
  emptyExperience,
  emptyPeriod,
  hasEducationEntry,
  hasExperienceEntry,
} from "./applicantPayload";
import {
  PROFILE_SECTIONS,
  MAX_EXPERIENCE_PERIODS,
  docStatusRollup,
  entryNeedsEvidence,
  experienceMonths,
  experiencePeriods,
  formatDuration,
  summarizeExperience,
  totalExperienceMonths,
} from "../applicants/applicantLogic";
import { DOC_FILE_STATUS_LABELS, labelForStatus } from "../applicants/applicantLabels";
import { IDENTITY_WARNINGS, compareHolder } from "../applicants/identityCheck";
import { chipsTone, toneRowSx } from "../applicants/rowTone";
import { HolderWarning } from "../applicants/IdentityNotice";

// Repeatable formación / experiencia editors, shared by the registration wizard
// (ApplicantRegistrationForm) and the self-service profile (ApplicantContinue) so
// both render an identical list. `onChange` always receives the whole next array.
//
// Each entry carries its OWN supporting document: the certificate is attached
// right here, next to the formación / experiencia it certifies, and travels as a
// profile document tagged with the entry's id. An entry marked "en curso" or
// "actualmente" owes no certificate — there is nothing to certify yet — so its
// upload is offered but never demanded.

const CHIP_COLOR = {
  UPLOADED: "info",
  VALIDATED: "success",
  REJECTED: "error",
};

// Shell around one entry. Collapsed by default so a hoja de vida with several
// degrees and jobs reads as a short list instead of a wall of fields; the header
// carries enough to identify the row (what it is) and its documental state
// (whether it still needs something) without opening it. Removing the only
// remaining row leaves a blank one behind rather than an empty section.
function EntryCard({ title, index, summary, statusChip, expanded, onToggle, onRemove, canRemove, disabled, children }) {
  const label = `${title} ${index + 1}`;
  return (
    <Box sx={{ mb: 1.5, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={onToggle}
        sx={{ px: 1, py: 1, cursor: "pointer", ...toneRowSx(chipsTone(statusChip)) }}
      >
        <IconButton size="small" aria-label={`${expanded ? "Contraer" : "Expandir"} ${label}`}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{summary}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5} flexShrink={0}>{statusChip}</Stack>
        {canRemove && (
          <Tooltip title="Eliminar">
            <span>
              <IconButton
                size="small"
                // Sin esto, borrar la fila también la abriría/cerraría.
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                disabled={disabled}
                aria-label={`Eliminar ${label}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Grid container spacing={2}>{children}</Grid>
        </Box>
      </Collapse>
    </Box>
  );
}

// The certificate of ONE entry: what is already stored (with its validation
// state and, if rejected, why), plus the slots to attach or replace it.
function EntryEvidence({ entry, section, doc, existing = [], pending = [], onPick, onView, disabled }) {
  if (!doc) return null;
  const required = entryNeedsEvidence(entry, section) && doc.required !== false;
  const status = docStatusRollup({ files: existing });
  const rejections = [...new Set(
    existing.filter((a) => a && a.status === "REJECTED" && a.rejectionComments)
      .map((a) => String(a.rejectionComments).trim())
  )];

  return (
    <Grid item xs={12}>
      <Box sx={{ mt: 1, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.label}</Typography>
          {existing.length > 0 ? (
            <Chip size="small" color={CHIP_COLOR[status] || "default"} label={labelForStatus(DOC_FILE_STATUS_LABELS, status)} />
          ) : required ? (
            <Chip size="small" color="warning" variant="outlined" label="OBLIGATORIO" />
          ) : (
            <Chip size="small" variant="outlined" label="Opcional" />
          )}
        </Stack>
        {!required && (
          <Typography variant="caption" color="text.secondary" display="block">
            {section === PROFILE_SECTIONS.EDUCATION
              ? "Estudios en curso: no se exige acta de grado todavía. Puedes adjuntar una constancia de estudios si la tienes."
              : "Empleo actual: no se exige certificado de terminación. Puedes adjuntar una constancia laboral vigente si la tienes."}
          </Typography>
        )}
        {doc.description && required && (
          <Typography variant="caption" color="text.secondary" display="block">{doc.description}</Typography>
        )}
        {status === "REJECTED" && (
          <Alert severity="error" sx={{ my: 0.5, py: 0 }}>
            {rejections.length > 0
              ? rejections.map((m, i) => <Typography key={i} variant="body2">{m}</Typography>)
              : <Typography variant="body2">Este documento fue rechazado.</Typography>}
            <Typography variant="caption" color="text.secondary" display="block">
              Vuelve a cargarlo corregido.
            </Typography>
          </Alert>
        )}
        <DocumentSlots
          docDef={doc}
          storedFiles={existing}
          pending={pending}
          disabled={disabled}
          onView={onView}
          onPick={(slot, file, replaceFileId) => onPick?.(entry.id, slot, file, replaceFileId)}
        />
      </Box>
    </Grid>
  );
}

// "N registros sin su soporte" — counts only the entries that actually owe one.
function EvidenceWarning({ pendingCount, noun, nounPlural, docLabel }) {
  if (!pendingCount) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {pendingCount} {pendingCount === 1 ? noun : nounPlural} sin su «{docLabel}».
      Sin soporte, {pendingCount === 1 ? "ese registro no será tenido" : "esos registros no serán tenidos"} en cuenta.
    </Alert>
  );
}

function AddEntryButton({ label, onClick, disabled, count }) {
  const atMax = count >= MAX_PROFILE_ENTRIES;
  return (
    <Box>
      <Button size="small" startIcon={<AddIcon />} onClick={onClick} disabled={disabled || atMax}>
        {label}
      </Button>
      {atMax && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Máximo {MAX_PROFILE_ENTRIES} registros.
        </Typography>
      )}
    </Box>
  );
}

// Generic list plumbing: update one entry, append a blank one, remove one
// (never leaving the list empty). Rows start collapsed; a row the applicant just
// added opens on its own, since clicking "Agregar" and getting a closed bar with
// nothing to type into reads as if the button did nothing.
function useEntryList(items, onChange, makeEmpty) {
  const list = Array.isArray(items) && items.length ? items : [makeEmpty()];
  const [open, setOpen] = React.useState({});
  return {
    list,
    isOpen: (id) => !!open[id],
    toggle: (id) => setOpen((o) => ({ ...o, [id]: !o[id] })),
    update: (i, key, value) => onChange(list.map((e, idx) => (idx === i ? { ...e, [key]: value } : e))),
    add: () => {
      const entry = makeEmpty();
      onChange([...list, entry]);
      setOpen((o) => ({ ...o, [entry.id]: true }));
    },
    remove: (i) => {
      const next = list.filter((_, idx) => idx !== i);
      onChange(next.length ? next : [makeEmpty()]);
    },
  };
}

// Chip shown on the collapsed header so the applicant can tell, at a glance,
// which rows still need something without opening every one of them.
function useHeaderChip({ evidenceDoc, filesForEntry, pendingFor, section }) {
  return (entry) => {
    if (!evidenceDoc) return null;
    const existing = filesForEntry(entry.id);
    if (existing.length) {
      const status = docStatusRollup({ files: existing });
      const chip = <Chip key="st" size="small" color={CHIP_COLOR[status] || "default"} label={labelForStatus(DOC_FILE_STATUS_LABELS, status)} />;
      // Its certificate seems someone else's: said on the closed row too.
      const notTheirs = existing.some((f) => IDENTITY_WARNINGS.has(f && f.identity && f.identity.status));
      return notTheirs
        ? [chip, <Chip key="id" size="small" color="error" label="Revisa el titular" />]
        : chip;
    }
    if (pendingFor(entry.id).some(Boolean)) {
      return <Chip size="small" color="info" variant="outlined" label="Por guardar" />;
    }
    if (!entryNeedsEvidence(entry, section)) return null;
    return <Chip size="small" color="warning" variant="outlined" label="Falta soporte" />;
  };
}

// Institution field. The catalog (config/applicants.institutions, loaded from the
// CSV an admin uploads) is a CLOSED list: while it has entries the applicant may
// only pick from it. The exception is a degree obtained abroad — a national
// catalog can't list foreign universities — where free text is accepted.
//
// The OPEN levels are the other exception: colegios (primaria / secundaria) and
// the entities behind diplomados / certificaciones aren't in that catalog, so
// the name is free text, suggested from what earlier applicants registered — a
// name that isn't there joins the list on save.
const openFilter = (opts, state) => {
  const q = loose(state.inputValue);
  if (q.length < 2) return [];
  return opts.filter((o) => loose(o).includes(q)).slice(0, 50);
};

const SCHOOL_LEVELS = ["Primaria", "Secundaria"];

function InstitutionField({ value, level, institutions, openInstitutions, openLevel, disabled, foreign, onChange }) {
  const options = Array.isArray(institutions) ? institutions : [];
  const closed = options.length > 0 && !foreign && !openLevel;

  if (openLevel && !foreign) {
    return (
      <Autocomplete
        freeSolo
        options={Array.isArray(openInstitutions) ? openInstitutions : []}
        // The typed text IS the value: only the input is controlled, so each
        // keystroke doesn't reset the suggestion popup.
        value={null}
        inputValue={value || ""}
        disabled={disabled}
        onInputChange={(_ev, next, reason) => { if (reason !== "reset" || next) onChange(next || ""); }}
        onChange={(_ev, next) => { if (next) onChange(next); }}
        filterOptions={openFilter}
        renderInput={(params) => (
          <TextField
            {...params}
            label={SCHOOL_LEVELS.includes(level) ? "Colegio o institución educativa" : "Institución o entidad que lo otorgó"}
            helperText="Escríbelo como aparece en tu diploma o certificado, o elígelo si ya aparece en la lista."
          />
        )}
      />
    );
  }

  if (!closed) {
    return (
      <TextField
        fullWidth
        disabled={disabled}
        label="Institución educativa"
        value={value || ""}
        onChange={(ev) => onChange(ev.target.value)}
        helperText={foreign && options.length > 0
          ? "Institución del exterior: escríbela como aparece en el título."
          : undefined}
      />
    );
  }
  return (
    <Autocomplete
      options={options}
      value={value || null}
      disabled={disabled}
      onChange={(_ev, next) => onChange(next || "")}
      isOptionEqualToValue={(opt, val) => opt === val}
      // Long catalogs: only start suggesting once there's something to match on.
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        if (q.length < 2) return [];
        return opts.filter((o) => o.toLowerCase().includes(q)).slice(0, 50);
      }}
      noOptionsText="Escribe al menos 2 letras para buscar tu institución"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Institución educativa"
          helperText="Selecciónala de la lista oficial. ¿Estudiaste en el exterior? Marca «Título obtenido en el exterior»."
        />
      )}
    />
  );
}

// --- AI reading of the supporting document ----------------------------------
// Both lists can read their certificate with AI (extractExperienceCertificate /
// extractEducationCertificate) and prefill the entry, and both start a brand-new
// entry with the document alone. What differs per list — what "blank" means,
// how a read lands on the entry, the copy — comes in as parameters.

// Spanish copy for the error codes the readers return. NOT_A_CERTIFICATE and
// the fallback name the document each list expects.
const EXTRACTION_ERRORS = {
  OCR_FAILED: "No pudimos leer el texto del documento. Completa los datos a mano.",
  RATE_LIMIT_IP: "Se han leído muchos documentos en poco tiempo. Completa los datos a mano o inténtalo en unos minutos.",
  FILE_TOO_LARGE: "El archivo es demasiado grande para leerlo automáticamente. Completa los datos a mano.",
  UNSUPPORTED_MIME: "Solo se pueden leer automáticamente archivos PDF o imágenes. Completa los datos a mano.",
  ENCRYPTED_FILE: "El archivo está protegido con contraseña. Completa los datos a mano.",
};
const EXPERIENCE_COPY = {
  notACertificate: "El documento no parece un certificado laboral. Completa los datos a mano.",
  fallback: "No pudimos leer el certificado automáticamente. Completa los datos a mano.",
  applied: "Datos extraídos del certificado, revísalos antes de guardar.",
  lowConfidence: " La lectura no fue del todo clara: verifica cada periodo.",
  reading: "Leyendo certificado…",
  useData: "Usar datos del certificado",
};
const EDUCATION_COPY = {
  notACertificate: "El documento no parece un acta de grado ni un diploma. Completa los datos a mano.",
  fallback: "No pudimos leer el documento automáticamente. Completa los datos a mano.",
  applied: "Datos extraídos del documento, revísalos antes de guardar.",
  lowConfidence: " La lectura no fue del todo clara: verifica cada dato.",
  reading: "Leyendo documento…",
  useData: "Usar datos del documento",
};
const extractionError = (code, copy) =>
  (code === "NOT_A_CERTIFICATE" ? copy.notACertificate : EXTRACTION_ERRORS[code]) || copy.fallback;

const LOW_CONFIDENCE = 0.6;
const DEFAULT_ACCEPT = ["application/pdf", "image/*"];
const acceptOf = (doc) => (doc?.acceptedMime?.length ? doc.acceptedMime : DEFAULT_ACCEPT).join(",");

// The read state of every entry, and what to do with each result:
//   - blank entry            → apply it ("applied")
//   - entry that agrees      → just confirm it ("confirmed"), e.g. the diploma
//                              after the acta of the same degree
//   - entry with other data  → offer it, never overwrite unasked ("offer")
//   - failure                → a warning; manual entry keeps working
function useCertificateReader({ list, onChange, extract, isBlank, isFresh = isBlank, apply, sameAs, copy, evidenceDoc, filesForEntry, pendingFor }) {
  const [ai, setAi] = React.useState({});
  // Entries past the certificate-first step (read finished, or "llenar a mano").
  const [revealed, setRevealed] = React.useState({});
  // Latest request per entry: a slower earlier read must not land after a newer one.
  const reqSeq = React.useRef({});
  // The entries as of the last render, for results that arrive later.
  const listRef = React.useRef(list);
  listRef.current = list;

  const reveal = (id) => setRevealed((prev) => ({ ...prev, [id]: true }));
  const setAiFor = (id, value) => setAi((prev) => ({ ...prev, [id]: value }));
  const commit = (id, fn) => onChange(listRef.current.map((e) => (e.id === id ? fn(e) : e)));

  const read = async (entryId, payload) => {
    const seq = (reqSeq.current[entryId] || 0) + 1;
    reqSeq.current[entryId] = seq;
    setAiFor(entryId, { status: "loading" });
    let out;
    try { out = await extract(payload); } catch (_) { out = { error: "EXTRACTION_FAILED" }; }
    if (reqSeq.current[entryId] !== seq) return;
    reveal(entryId);
    if (!out || !out.extraction) {
      // AI switched off by the admin: say nothing, the form works as before.
      setAiFor(entryId, out && out.error === "AI_DISABLED" ? null : { status: "error", message: extractionError(out && out.error, copy) });
      return;
    }
    const x = out.extraction;
    const entry = listRef.current.find((e) => e.id === entryId);
    if (!entry) return;
    if (isBlank(entry)) {
      commit(entryId, (e) => apply(e, x));
      setAiFor(entryId, { status: "applied", lowConfidence: x.confidence < LOW_CONFIDENCE, extraction: x });
    } else if (sameAs && sameAs(entry, x)) {
      setAiFor(entryId, { status: "confirmed", extraction: x });
    } else {
      setAiFor(entryId, { status: "offer", extraction: x });
    }
  };

  const applyOffer = (entryId) => {
    const x = ai[entryId] && ai[entryId].extraction;
    if (x) commit(entryId, (e) => apply(e, x));
    setAiFor(entryId, { status: "applied", lowConfidence: !!x && x.confidence < LOW_CONFIDENCE, extraction: x });
  };

  // A brand-new entry (nothing typed, no file stored or picked) starts with the
  // document alone — only when there is a document slot to fill. The step stays
  // up while its document is being read, then gives way to the form.
  const certificateFirst = (e) => !!evidenceDoc && !revealed[e.id]
    && isFresh(e) && filesForEntry(e.id).length === 0
    && (ai[e.id]?.status === "loading" || !pendingFor(e.id).some(Boolean));

  return { ai, setAiFor, reveal, read, applyOffer, certificateFirst };
}

// What the AI read of this entry's document: in progress, applied, confirmed,
// offered (the entry already had data, so nothing is overwritten unasked) or failed.
function ExtractionNotice({ state, copy, offerText, disabled, onApply, onDismiss }) {
  if (!state) return null;
  if (state.status === "loading") {
    return (
      <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 1.5 }}>
        {copy.reading}
      </Alert>
    );
  }
  if (state.status === "applied") {
    return (
      <Alert severity="info" onClose={onDismiss} sx={{ mb: 1.5 }}>
        {copy.applied}
        {state.lowConfidence && copy.lowConfidence}
      </Alert>
    );
  }
  if (state.status === "confirmed") {
    return (
      <Alert severity="success" onClose={onDismiss} sx={{ mb: 1.5 }}>
        Datos confirmados con el documento.
      </Alert>
    );
  }
  if (state.status === "offer") {
    return (
      <Alert
        severity="info"
        onClose={onDismiss}
        sx={{ mb: 1.5 }}
        action={<Button size="small" color="inherit" disabled={disabled} onClick={onApply}>{copy.useData}</Button>}
      >
        {offerText(state.extraction)} ¿Reemplazar lo que escribiste?
      </Alert>
    );
  }
  return (
    <Alert severity="warning" onClose={onDismiss} sx={{ mb: 1.5 }}>{state.message}</Alert>
  );
}

// Whose document the AI says it is, against the applicant (`person`), once a
// read has landed. Nothing while reading, on failure, or when it matches.
function ReadHolderWarning({ state, person }) {
  const x = state && state.extraction;
  if (!x || !person) return null;
  return <HolderWarning holder={x} check={compareHolder(x, person)} />;
}

// First step of a NEW entry: only the supporting document. Once it's read, the
// entry opens with what it says, ready to review; "llenar a mano" covers the
// applicant who doesn't have it yet — nothing here ever blocks.
function CertificateFirst({ reading, readingText, readingHint, title, hint, manualLabel, onManual, disabled, children }) {
  return (
    <Grid item xs={12}>
      <Box sx={{ border: 2, borderStyle: "dashed", borderColor: "divider", borderRadius: 1, p: 3, textAlign: "center" }}>
        {reading ? (
          <Stack alignItems="center" spacing={1}>
            <CircularProgress size={28} />
            <Typography variant="body2">{readingText}</Typography>
            <Typography variant="caption" color="text.secondary">{readingHint}</Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">{hint}</Typography>
            {children}
            <Button size="small" onClick={onManual} disabled={disabled}>{manualLabel}</Button>
          </Stack>
        )}
      </Box>
    </Grid>
  );
}

function PickFileButton({ label, accept, ariaLabel, disabled, variant = "contained", onFile }) {
  return (
    <Button component="label" variant={variant} startIcon={<UploadFileIcon />} disabled={disabled}>
      {label}
      <input
        hidden
        type="file"
        accept={accept}
        aria-label={ariaLabel}
        onClick={(e) => { e.target.value = ""; }}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onFile(f); }}
      />
    </Button>
  );
}

// --- Formación ---------------------------------------------------------------

// Accent-, case- and punctuation-insensitive form, to tell whether a read agrees
// with what the entry already says.
const loose = (s) => String(s || "")
  .normalize("NFD").replace(/[̀-ͯ]/g, "")
  .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const isBlankEducation = (e) => !hasEducationEntry(e);

// The read agrees with the entry when it names the same degree at the same
// institution — typically the diploma read after the acta of the same degree.
const sameEducation = (e, x) => !!x.degree && loose(e.degree) === loose(x.degree)
  && [x.institution, x.institutionRead].some((name) => name && loose(name) === loose(e.institution));

// Where the institution field stands after a read: the entry the function
// matched (catalog, or the open list for an open level), or the name as written
// when the field is free text (open level, foreign degree, no catalog). An
// unmatched name in a closed catalog leaves the field blank for the applicant
// to pick.
const institutionFromRead = (x, { open, catalogSize }) => {
  if (x.institutionMatched) return x.institution;
  if (open || x.foreignDegree || catalogSize === 0) return x.institutionRead || x.institution || "";
  return "";
};

const applyEducation = (entry, x, { levels, institutions, openLevels = [] }) => {
  const level = x.level && levels.includes(x.level) ? x.level : entry.level || "";
  const open = !!x.open || openLevels.includes(level);
  return {
    ...entry,
    level,
    degree: x.degree || entry.degree || "",
    institution: x.institutionRead || x.institution
      ? institutionFromRead(x, { open, catalogSize: institutions.length })
      : entry.institution || "",
    graduationDate: x.inProgress ? "" : (x.graduationDate || entry.graduationDate || ""),
    inProgress: !!x.inProgress,
    foreignDegree: !!x.foreignDegree,
    convalidated: !!x.foreignDegree && !!x.convalidated,
  };
};

// The documents a formación can be read from. The applicant picks which one(s)
// they have — acta, diploma or both — and each gets its own upload.
const EDUCATION_DOCS = [
  { key: "acta", label: "Acta de grado" },
  { key: "diploma", label: "Diploma" },
];

function EducationDocPicker({ accept, maxFiles, canRead, disabled, onSubmit }) {
  // An entry that holds a single file can only be read from one document.
  const single = maxFiles === 1;
  const [kinds, setKinds] = React.useState(["acta"]);
  const [files, setFiles] = React.useState({});
  const chosen = EDUCATION_DOCS.filter((d) => kinds.includes(d.key) && files[d.key]);
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
      <ToggleButtonGroup
        size="small"
        color="primary"
        value={single ? kinds[0] : kinds}
        exclusive={single}
        disabled={disabled}
        aria-label="Documentos que tienes"
        // Nunca vacío: siempre hay al menos un documento por subir.
        onChange={(_ev, next) => {
          const list = single ? (next ? [next] : []) : next;
          if (list && list.length) setKinds(list);
        }}
      >
        {EDUCATION_DOCS.map((d) => <ToggleButton key={d.key} value={d.key}>{d.label}</ToggleButton>)}
      </ToggleButtonGroup>
      {EDUCATION_DOCS.filter((d) => kinds.includes(d.key)).map((d) => (
        <Stack key={d.key} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
          <PickFileButton
            label={files[d.key] ? `Cambiar ${d.label.toLowerCase()}` : `Subir ${d.label.toLowerCase()} (PDF o imagen)`}
            variant={files[d.key] ? "outlined" : "contained"}
            accept={accept}
            ariaLabel={d.label}
            disabled={disabled}
            onFile={(f) => setFiles((prev) => ({ ...prev, [d.key]: f }))}
          />
          {files[d.key] && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 240 }}>
              {files[d.key].name}
            </Typography>
          )}
        </Stack>
      ))}
      <Button
        variant="contained"
        disabled={disabled || chosen.length === 0}
        onClick={() => onSubmit(Object.fromEntries(chosen.map((d) => [d.key, files[d.key]])))}
      >
        {canRead ? (chosen.length > 1 ? "Leer documentos" : "Leer documento") : "Continuar"}
      </Button>
    </Stack>
  );
}

export function EducationList({
  items,
  levels = [],
  institutions = [],
  // Institutions of the open levels registered so far, and those levels
  // (primaria, secundaria, diplomado / certificación, plus any the admin adds).
  openInstitutions = [],
  openLevels = [],
  onChange,
  disabled = false,
  evidenceDoc = null,
  filesForEntry = () => [],
  pendingFor = () => [],
  onPickEvidence,
  onViewFile,
  // ({ acta?, diploma? }) => Promise<{ extraction } | { error }>. When given,
  // the documents are read and fill (or offer to fill) the entry.
  onExtractCertificate,
  // { nuip, firstName, lastName } — to warn when a document read seems to be
  // someone else's.
  person = null,
}) {
  const { list, update, add, remove, isOpen, toggle } = useEntryList(items, onChange, emptyEducation);
  const section = PROFILE_SECTIONS.EDUCATION;
  const headerChip = useHeaderChip({ evidenceDoc, filesForEntry, pendingFor, section });
  const reader = useCertificateReader({
    list,
    onChange,
    extract: onExtractCertificate,
    isBlank: isBlankEducation,
    apply: (e, x) => applyEducation(e, x, { levels, institutions, openLevels }),
    sameAs: sameEducation,
    copy: EDUCATION_COPY,
    evidenceDoc,
    filesForEntry,
    pendingFor,
  });
  // Lo que identifica la fila cerrada: el título y dónde se cursó.
  const summaryOf = (e) => [e.degree, e.institution].filter(Boolean).join(" · ") || "Sin completar";
  const pendingCount = list
    .filter(hasEducationEntry)
    .filter((e) => entryNeedsEvidence(e, section)
      && filesForEntry(e.id).length === 0
      && pendingFor(e.id).filter(Boolean).length === 0).length;

  // A document picked in its slot (replacing or adding one) is read as well.
  const onPick = (entryId, slot, file, replaceFileId) => {
    onPickEvidence?.(entryId, slot, file, replaceFileId);
    if (file && onExtractCertificate) reader.read(entryId, slot === 0 ? { acta: file } : { diploma: file });
    else if (file) reader.reveal(entryId);
  };
  // The first step's documents become the entry's certificates, in slot order,
  // and are read together in one request.
  const onFirstDocs = (entryId, docs) => {
    EDUCATION_DOCS.map((d) => docs[d.key]).filter(Boolean)
      .forEach((file, slot) => onPickEvidence?.(entryId, slot, file, null));
    if (onExtractCertificate) reader.read(entryId, docs);
    else reader.reveal(entryId);
  };
  const isOpenLevel = (e) => !!e.level && openLevels.includes(e.level);
  // Nombre leído que no está en la lista oficial: el campo queda vacío y se dice por qué.
  const unmatchedInstitution = (e) => {
    const x = reader.ai[e.id]?.extraction;
    if (!x || !x.institutionRead || x.institutionMatched || e.institution) return "";
    if (e.foreignDegree || isOpenLevel(e) || institutions.length === 0) return "";
    return x.institutionRead;
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Puedes registrar todos tus niveles de educación. Cada uno se respalda con su propia acta de
        grado o diploma, que adjuntas dentro del registro: los estudios sin soporte documental no se
        tendrán en cuenta.
        {onExtractCertificate && " Al adjuntar el acta o el diploma leeremos sus datos automáticamente para que solo tengas que revisarlos."}
      </Alert>
      {evidenceDoc && (
        <EvidenceWarning
          pendingCount={pendingCount}
          noun="formación"
          nounPlural="formaciones"
          docLabel={evidenceDoc.label}
        />
      )}
      {list.map((e, i) => (
        <EntryCard
          key={e.id || i}
          title="Formación"
          index={i}
          summary={summaryOf(e)}
          statusChip={headerChip(e)}
          expanded={isOpen(e.id)}
          onToggle={() => toggle(e.id)}
          canRemove={list.length > 1}
          disabled={disabled}
          onRemove={() => remove(i)}
        >
          {reader.certificateFirst(e) ? (
            <CertificateFirst
              reading={reader.ai[e.id]?.status === "loading"}
              readingText={EDUCATION_COPY.reading}
              readingHint="En unos segundos verás el título, la institución y la fecha de grado para revisarlos."
              title="Empieza por el acta de grado o el diploma"
              hint="Elige cuál tienes (o ambos). Leeremos el nivel, el título, la institución y la fecha de grado; después podrás revisarlos y corregirlos."
              manualLabel="No tengo ninguno aún, llenar a mano"
              onManual={() => reader.reveal(e.id)}
              disabled={disabled}
            >
              <EducationDocPicker
                accept={acceptOf(evidenceDoc)}
                maxFiles={Math.floor(Number(evidenceDoc?.maxFiles))}
                canRead={!!onExtractCertificate}
                disabled={disabled}
                onSubmit={(docs) => onFirstDocs(e.id, docs)}
              />
            </CertificateFirst>
          ) : (
            <>
          <Grid item xs={12}>
            <ExtractionNotice
              state={reader.ai[e.id]}
              copy={EDUCATION_COPY}
              disabled={disabled}
              offerText={(x) => `El documento indica ${x.degree ? `«${x.degree}»` : "un título"}${x.institutionRead ? ` de «${x.institutionRead}»` : ""}.`}
              onDismiss={() => reader.setAiFor(e.id, null)}
              onApply={() => reader.applyOffer(e.id)}
            />
            <ReadHolderWarning state={reader.ai[e.id]} person={person} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel>Nivel académico</InputLabel>
              <Select label="Nivel académico" value={e.level || ""} onChange={(ev) => update(i, "level", ev.target.value)}>
                {levels.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth disabled={disabled} label="Título obtenido" value={e.degree || ""}
              onChange={(ev) => update(i, "degree", ev.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InstitutionField
              value={e.institution}
              institutions={institutions}
              level={e.level}
              openInstitutions={openInstitutions}
              openLevel={isOpenLevel(e)}
              disabled={disabled}
              foreign={!!e.foreignDegree}
              onChange={(v) => update(i, "institution", v)}
            />
            {unmatchedInstitution(e) && (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                El documento dice «{unmatchedInstitution(e)}»: selecciónala de la lista oficial.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Fecha de grado"
              disabled={disabled || !!e.inProgress}
              InputLabelProps={{ shrink: true }}
              value={e.inProgress ? "" : (e.graduationDate || "")}
              onChange={(ev) => update(i, "graduationDate", ev.target.value)}
              helperText={e.inProgress ? "Estudios en curso: sin fecha de grado." : "Opcional si aún estás estudiando."} />
          </Grid>
          <Grid item xs={12}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox size="small" disabled={disabled} checked={!!e.inProgress}
                    onChange={(ev) => update(i, "inProgress", ev.target.checked)} />
                }
                label="Actualmente estudiando (en curso)"
              />
              <FormControlLabel
                control={
                  <Checkbox size="small" disabled={disabled} checked={!!e.foreignDegree}
                    onChange={(ev) => update(i, "foreignDegree", ev.target.checked)} />
                }
                label="Título obtenido en el exterior"
              />
              {/* Convalidación sólo aplica a un título extranjero. */}
              {e.foreignDegree && (
                <FormControlLabel
                  control={
                    <Checkbox size="small" disabled={disabled} checked={!!e.convalidated}
                      onChange={(ev) => update(i, "convalidated", ev.target.checked)} />
                  }
                  label="Convalidado en Colombia"
                />
              )}
            </Stack>
            {e.foreignDegree && !e.convalidated && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Un título obtenido en el exterior debe estar convalidado ante el Ministerio de
                Educación Nacional para ser tenido en cuenta. Si ya lo convalidaste, marca la casilla
                y adjunta la resolución de convalidación junto con el título.
              </Alert>
            )}
          </Grid>
          <EntryEvidence
            entry={e}
            section={section}
            doc={evidenceDoc}
            existing={filesForEntry(e.id)}
            pending={pendingFor(e.id)}
            onPick={onPick}
            onView={onViewFile}
            disabled={disabled}
          />
            </>
          )}
        </EntryCard>
      ))}
      <AddEntryButton label="Agregar otra formación" onClick={add} disabled={disabled} count={list.length} />
    </Box>
  );
}

// --- Experiencia -------------------------------------------------------------

const hasPeriodData = (p) => !!(p && (p.position || p.startDate || p.endDate || p.current));
// Nothing typed yet besides, maybe, the description: safe to fill from the certificate.
const isBlankExperience = (e) => !e.company && !e.nit && !(e.periods || []).some(hasPeriodData);

// The entry as the form holds it: always with a periods list (a legacy entry is
// its single period) and with the summary fields kept in step, since the
// evidence rules read `current` straight off the entry.
const withPeriods = (e) => {
  const periods = experiencePeriods(e);
  return { ...e, periods: periods.length ? periods : [emptyPeriod()] };
};
const resummarize = (e) => summarizeExperience(e);

// What the certificate said, turned into the entry's fields.
const applyExtraction = (entry, x) => resummarize({
  ...entry,
  company: x.company || entry.company || "",
  nit: x.nit || entry.nit || "",
  periods: x.periods && x.periods.length
    ? x.periods.map((p) => ({ ...emptyPeriod(), ...p }))
    : entry.periods,
});

function PeriodRow({ period, index, canRemove, disabled, onChange, onRemove }) {
  const set = (key, value) => onChange({ ...period, [key]: value, ...(key === "current" && value ? { endDate: "" } : {}) });
  return (
    <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
      <Grid item xs={12} md={4}>
        <TextField fullWidth size="small" disabled={disabled} label={`Cargo (periodo ${index + 1})`}
          value={period.position || ""} onChange={(ev) => set("position", ev.target.value)} />
      </Grid>
      <Grid item xs={6} md={2.5}>
        <TextField fullWidth size="small" disabled={disabled} type="date" label="Desde"
          InputLabelProps={{ shrink: true }} value={period.startDate || ""}
          onChange={(ev) => set("startDate", ev.target.value)} />
      </Grid>
      <Grid item xs={6} md={2.5}>
        <TextField fullWidth size="small" type="date" label="Hasta"
          disabled={disabled || !!period.current}
          InputLabelProps={{ shrink: true }}
          value={period.current ? "" : (period.endDate || "")}
          onChange={(ev) => set("endDate", ev.target.value)} />
      </Grid>
      <Grid item xs={10} md={2.5}>
        <FormControlLabel
          control={
            <Checkbox size="small" disabled={disabled} checked={!!period.current}
              onChange={(ev) => set("current", ev.target.checked)} />
          }
          label="Actualmente"
        />
      </Grid>
      <Grid item xs={2} md={0.5}>
        {canRemove && (
          <Tooltip title="Eliminar periodo">
            <span>
              <IconButton size="small" disabled={disabled} onClick={onRemove}
                aria-label={`Eliminar periodo ${index + 1}`}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
}

export function ExperienceList({
  items,
  onChange,
  disabled = false,
  evidenceDoc = null,
  filesForEntry = () => [],
  pendingFor = () => [],
  onPickEvidence,
  onViewFile,
  // (file) => Promise<{ extraction } | { error }>. When given, picking a
  // certificate reads it and fills (or offers to fill) the entry.
  onExtractCertificate,
  // { nuip, firstName, lastName } — to warn when a certificate read seems to
  // be someone else's.
  person = null,
}) {
  const { list: rawList, add, remove, isOpen, toggle } = useEntryList(items, onChange, emptyExperience);
  const list = rawList.map(withPeriods);
  const section = PROFILE_SECTIONS.EXPERIENCE;
  const headerChip = useHeaderChip({ evidenceDoc, filesForEntry, pendingFor, section });
  const reader = useCertificateReader({
    list,
    onChange,
    extract: onExtractCertificate,
    isBlank: isBlankExperience,
    isFresh: (e) => isBlankExperience(e) && !e.description,
    apply: applyExtraction,
    copy: EXPERIENCE_COPY,
    evidenceDoc,
    filesForEntry,
    pendingFor,
  });

  const setEntry = (i, next) => onChange(list.map((e, idx) => (idx === i ? resummarize(next) : e)));
  const update = (i, key, value) => setEntry(i, { ...list[i], [key]: value });
  const updatePeriod = (i, pi, period) =>
    setEntry(i, { ...list[i], periods: list[i].periods.map((p, idx) => (idx === pi ? period : p)) });
  const addPeriod = (i) => setEntry(i, { ...list[i], periods: [...list[i].periods, emptyPeriod()] });
  const removePeriod = (i, pi) => {
    const periods = list[i].periods.filter((_, idx) => idx !== pi);
    setEntry(i, { ...list[i], periods: periods.length ? periods : [emptyPeriod()] });
  };

  const onPick = (entryId, slot, file, replaceFileId) => {
    onPickEvidence?.(entryId, slot, file, replaceFileId);
    if (file && onExtractCertificate) reader.read(entryId, file);
    else if (file) reader.reveal(entryId);
  };

  // Lo que identifica la fila cerrada: la empresa, el cargo más reciente y el tiempo.
  const summaryOf = (e) => {
    const months = experienceMonths(e.periods);
    const text = [e.company, e.position].filter(Boolean).join(" · ");
    if (!text) return "Sin completar";
    return months > 0 ? `${text} — ${formatDuration(months)}` : text;
  };
  const filled = list.filter(hasExperienceEntry);
  const totalMonths = totalExperienceMonths(filled);
  const pendingCount = filled
    .filter((e) => entryNeedsEvidence(e, section)
      && filesForEntry(e.id).length === 0
      && pendingFor(e.id).filter(Boolean).length === 0).length;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Registra una experiencia por cada empresa y respáldala con su certificado laboral. Si el
        certificado incluye varios cargos o periodos, agrégalos todos dentro de la misma experiencia.
        {onExtractCertificate && " Al adjuntar el certificado leeremos sus datos automáticamente para que solo tengas que revisarlos."}
        {" "}La experiencia sin soporte documental no se tendrá en cuenta.
      </Alert>
      {totalMonths > 0 && (
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Experiencia total: <strong>{formatDuration(totalMonths)}</strong>
          <Typography component="span" variant="caption" color="text.secondary">
            {" "}(los periodos que se cruzan se cuentan una sola vez)
          </Typography>
        </Typography>
      )}
      {evidenceDoc && (
        <EvidenceWarning
          pendingCount={pendingCount}
          noun="experiencia"
          nounPlural="experiencias"
          docLabel={evidenceDoc.label}
        />
      )}
      {list.map((e, i) => (
        <EntryCard
          key={e.id || i}
          title="Experiencia"
          index={i}
          summary={summaryOf(e)}
          statusChip={headerChip(e)}
          expanded={isOpen(e.id)}
          onToggle={() => toggle(e.id)}
          canRemove={list.length > 1}
          disabled={disabled}
          onRemove={() => remove(i)}
        >
          {reader.certificateFirst(e) ? (
            <CertificateFirst
              reading={reader.ai[e.id]?.status === "loading"}
              readingText={EXPERIENCE_COPY.reading}
              readingHint="En unos segundos verás la empresa y los periodos para revisarlos."
              title={`Empieza por el ${evidenceDoc?.label ? evidenceDoc.label.toLowerCase() : "certificado laboral"}`}
              hint="Leeremos la empresa, el NIT y los periodos; después podrás revisarlos y corregirlos."
              manualLabel="No tengo el certificado aún, llenar a mano"
              onManual={() => reader.reveal(e.id)}
              disabled={disabled}
            >
              <PickFileButton
                label="Subir certificado (PDF o imagen)"
                accept={acceptOf(evidenceDoc)}
                ariaLabel="Certificado laboral"
                disabled={disabled}
                onFile={(file) => onPick(e.id, 0, file, null)}
              />
            </CertificateFirst>
          ) : (
            <>
            <Grid item xs={12}>
              <ExtractionNotice
                state={reader.ai[e.id]}
                copy={EXPERIENCE_COPY}
                disabled={disabled}
                offerText={(x) => {
                  const n = (x.periods || []).length;
                  return `El certificado indica ${x.company ? `«${x.company}»` : "una empresa"}${n ? ` con ${n} ${n === 1 ? "periodo" : "periodos"}` : ""}.`;
                }}
                onDismiss={() => reader.setAiFor(e.id, null)}
                onApply={() => reader.applyOffer(e.id)}
              />
              <ReadHolderWarning state={reader.ai[e.id]} person={person} />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth disabled={disabled} label="Empresa" value={e.company || ""}
                onChange={(ev) => update(i, "company", ev.target.value)} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth disabled={disabled} label="NIT de la empresa" value={e.nit || ""}
                onChange={(ev) => update(i, "nit", ev.target.value)}
                helperText="Sin dígito de verificación. Opcional." />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Periodos</Typography>
                {experienceMonths(e.periods) > 0 && (
                  <Chip size="small" variant="outlined" label={formatDuration(experienceMonths(e.periods))} />
                )}
              </Stack>
              {e.periods.map((p, pi) => (
                <PeriodRow
                  key={pi}
                  period={p}
                  index={pi}
                  canRemove={e.periods.length > 1}
                  disabled={disabled}
                  onChange={(next) => updatePeriod(i, pi, next)}
                  onRemove={() => removePeriod(i, pi)}
                />
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={() => addPeriod(i)}
                disabled={disabled || e.periods.length >= MAX_EXPERIENCE_PERIODS}>
                Agregar periodo
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth disabled={disabled} multiline minRows={2} label="Descripción"
                value={e.description || ""} onChange={(ev) => update(i, "description", ev.target.value)} />
            </Grid>
            <EntryEvidence
              entry={e}
              section={section}
              doc={evidenceDoc}
              existing={filesForEntry(e.id)}
              pending={pendingFor(e.id)}
              onPick={onPick}
              onView={onViewFile}
              disabled={disabled}
            />
            </>
          )}
        </EntryCard>
      ))}
      <AddEntryButton label="Agregar otra experiencia" onClick={add} disabled={disabled} count={list.length} />
    </Box>
  );
}
